import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import DatePicker, { registerLocale } from 'react-datepicker';
import weekday from 'dayjs/plugin/weekday';
import IconButton from '@mui/material/IconButton';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { vi } from 'date-fns/locale/vi';

registerLocale('vi', vi);

dayjs.extend(weekday);

import Button from '../Button';

import * as DateTimePickerStyles from '../../components/DateTimePicker/DateTimePicker.styles';
import * as Styles from './WeekCalendar.styles';

export default function WeekCalendar({
  onChange,
  currentDate = new Date(),
  maxDate,
  minDate,
  holidays = [],
  hideWeekday = true,
}) {
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [currentWeek, setCurrentWeek] = useState(currentDate);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    setSelectedDate(currentDate);
    setCurrentWeek(currentDate);
  }, [currentDate]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const changeWeekHandle = (btnType) => {
    if (btnType === 'prev') {
      const prevDate = dayjs(currentWeek).subtract(1, 'week');
      setCurrentWeek(prevDate);
    }
    if (btnType === 'next') {
      const prevDate = dayjs(currentWeek).add(1, 'week');
      setCurrentWeek(prevDate);
    }
    setOpen(false);
  };

  const handleDateSelected = (_currentDate) => {
    setSelectedDate(_currentDate);
    if (onChange) {
      onChange(_currentDate);
    }
    setOpen(false);
  };

  const renderCells = () => {
    const startDate = dayjs(currentWeek).weekday(0);
    const endDate = dayjs(currentWeek).weekday(6);
    const rows = [];
    let days = [];
    let day = startDate;
    const _maxDate = Number(dayjs(maxDate).format('YYYYMMDD'));
    const _minDate = Number(dayjs(minDate).format('YYYYMMDD'));
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        let className =
          dayjs(selectedDate).format('D') === dayjs(day).format('D')
            ? 'selected'
            : '';
        let disabled = '';
        if (
          Number(dayjs(day).format('YYYYMMDD')) > _maxDate ||
          Number(dayjs(day).format('YYYYMMDD')) < _minDate ||
          ['Sun', 'Sat'].includes(dayjs(day).format('ddd')) ||
          holidays.includes(dayjs(day).format('YYYY-MM-DD'))
        ) {
          className = '';
          disabled = 'disabled';
        }
        const _currentDate = dayjs(day);
        days.push(
          <IconButton
            className={`cell ${className} ${disabled}`}
            key={day}
            onClick={() => handleDateSelected(_currentDate)}
          >
            <span className="inline-flex items-center justify-center flex-col w-7 h-7">
              <span className="text-[11px] font-bold leading-none">
                {dayjs(day).format('DD/MM')}
              </span>
            </span>
          </IconButton>
        );
        day = dayjs(day).add(1, 'day');
      }

      rows.push(
        <div className="flex justify-between gap-1" key={day}>
          {days}
        </div>
      );
      days = [];
    }

    return (
      <div className="flex justify-between items-center">
        <div>
          <IconButton
            onClick={() => changeWeekHandle('prev')}
            disabled={
              Number(dayjs(currentWeek).weekday(0).format('YYYYMMDD')) <
              _minDate
            }
            className="btn-navigation"
          >
            <PlayArrowIcon className="navigation prev" />
          </IconButton>
        </div>
        <div className="w-full">{rows}</div>
        <div>
          <IconButton
            onClick={() => changeWeekHandle('next')}
            disabled={
              Number(dayjs(currentWeek).weekday(6).format('YYYYMMDD')) >
              _maxDate
            }
            className="btn-navigation"
          >
            <PlayArrowIcon className="navigation next" />
          </IconButton>
        </div>
      </div>
    );
  };

  const handleOnChange = (date) => {
    setSelectedDate(date);
    if (onChange) {
      onChange(date);
    }
    setOpen(false);
  };

  const renderDayContents = (day) => {
    return (
      <span className="relative w-8 h-8 min-h-8 rounded-full inline-flex items-center justify-center day-content">
        {day}
      </span>
    );
  };

  const options = {
    selected: new Date(selectedDate),
    onChange: (date) => handleOnChange(date),
    timeInputLabel: '',
    showTimeInput: false,
    dateFormat: 'dd/MM/yyyy',
    showMonthDropdown: true,
    showYearDropdown: true,
    shouldCloseOnSelect: false,
    dropdownMode: 'select',
    inline: true,
    locale: 'vi',
    calendarStartDay: 0,
  };

  const isWeekday = (date) => {
    const day = dayjs(date).get('day');
    return day !== 0 && day !== 6;
  };

  if (hideWeekday) {
    options.filterDate = isWeekday;
  }

  if (minDate) {
    options.minDate = minDate;
  }

  if (maxDate) {
    options.maxDate = maxDate;
  }

  if (holidays?.length) {
    options.excludeDates = holidays.map((value) => new Date(value));
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const renderReminder = () => {
    const now = dayjs();
    if (now.format('YYYYMMDD') === dayjs(selectedDate).format('YYYYMMDD')) {
      if (
        Number(now.format('HHmm')) >= 1220 &&
        Number(now.format('HHmm')) <= 1615
      ) {
        return (
          <div className="text-center text-xs italic">
            Dữ liệu cập nhất đến 12:30 PM
          </div>
        );
      }
    }
    return null;
  };

  const renderFooter = () => {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center w-full">
          <IconButton
            onClick={handleToggle}
            ref={anchorRef}
            className="more-btn"
          >
            <HorizontalRuleIcon className="more-date" />
          </IconButton>
        </div>
        {renderReminder()}
      </div>
    );
  };

  return (
    <Styles.Wrapper>
      {renderCells()}
      {renderFooter()}
      <Styles.PopperWrapper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <DateTimePickerStyles.Wrapper>
                  <DatePicker
                    {...options}
                    renderDayContents={renderDayContents}
                  />
                  <div className="flex p-2 pb-3 pr-3 items-end justify-end">
                    <Button onClick={handleClose}>Đóng</Button>
                  </div>
                </DateTimePickerStyles.Wrapper>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Styles.PopperWrapper>
    </Styles.Wrapper>
  );
}
